import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'

// ─── /protect — public life insurance landing ────────────────────────────────
// Two forms on one page: a quote request and a recruiting inquiry. Both write a
// row to insurance_leads and then fire send-insurance-lead-to-fub, which is the
// shape OpenHouseLanding uses — row first so a FUB outage leaves something
// recoverable, then a fire-and-forget push whose failure never reaches the
// visitor.
//
// SchoolDisclosure is deliberately NOT mounted. This is not a real estate page
// and the ADRE school number has no business appearing on it.

// ─── Contacts — TO BE FILLED IN ──────────────────────────────────────────────
// Empty strings hide their footer button rather than rendering a dead link.
const DANAE_PHONE = '+16023001892'
const DANAE_EMAIL = 'azbroker@homeprosholdings.com'

// ─── Palette ─────────────────────────────────────────────────────────────────
const NAVY = '#1d2e42'
const GOLD = '#fdbf00'
const BLUE = '#147efb'
const PAGE = '#f5f5f5'
const WHITE = '#ffffff'
const INSET = '#f4f4f2'
const HAIRLINE = '#e8e8e8'
const SECONDARY = '#666'
const MUTED = '#999'
const ON_NAVY_BODY = '#B5D4F4'

const T = {
  en: {
    langEn: 'EN', langEs: 'ES',
    docTitle: 'Life insurance — Danae Marquez',
    hero: {
      initials: 'DM',
      name: 'Danae Marquez',
      quote: "Life insurance isn't for the person who dies. It's for the people who keep living.",
      statNumber: '~100 million',
      statText: 'U.S. adults live with no coverage or not enough of it. Most think it costs 3x what it actually does.',
      forkLabel: 'Two reasons people are on this page —',
      ctaQuote: 'Protect my family ↓',
      ctaJoin: 'Join the team ↓',
    },
    quote: {
      eyebrow: 'FREE, NO OBLIGATION',
      title: 'Find out what it actually costs',
      body: 'Most families are surprised how little covers them. Four questions, 30 seconds, and we call you back with real numbers.',
      name: 'Full name', phone: 'Phone', email: 'Email', age: 'Age',
      interestLabel: 'What are you looking for?',
      interest: { term: 'Term life', whole: 'Whole life', final_expense: 'Final expense', not_sure: 'Not sure yet' },
      submit: 'Send my info',
      sending: 'Sending…',
      finePrint: 'By submitting you agree to be contacted about coverage. No spam, no obligation.',
      success: "Got it. We'll call you soon.",
    },
    trust: [
      { title: 'Coverage that fits the budget', body: "Options across multiple carriers, not one company's shelf." },
      { title: 'Español disponible', body: 'Every conversation and document explained in your language.' },
      { title: 'Straight answers, fast', body: "Call, text, or email and you'll reach a real person on our team." },
    ],
    join: {
      badge: "We're growing",
      title: 'Build something of your own — and add income while you do it',
      body: "We're building a team of licensed life agents. No experience required — we'll walk you through licensing, training, and your first appointments.",
      boxes: [
        { label: 'Licensing help', value: 'Step-by-step' },
        { label: 'Live training', value: 'Weekly' },
        { label: 'Flexible hours', value: 'Full or part time' },
        { label: 'Bilingual team', value: 'EN / ES' },
      ],
      name: 'Full name', phone: 'Phone', email: 'Email',
      licensedLabel: 'Are you licensed?',
      licensedYes: 'Yes',
      licensedNo: 'No, but interested',
      about: 'Tell us a little about yourself',
      submit: "Let's talk",
      sending: 'Sending…',
      affiliation: 'This inquiry is for an Independent Contractor (1099) opportunity offered by a PHP independent affiliate.',
      success: "Got it. We'll reach out soon.",
    },
    footer: {
      name: 'Danae Marquez',
      title: 'Licensed life insurance producer · Arizona',
      license: 'License #22213864',
      call: 'Call', text: 'Text', email: 'Email',
      disclaimer: 'Insurance products are offered separately from and are not affiliated with HomePros or HomePros Advantage real estate services.',
    },
    errors: {
      name: 'Please enter your name',
      contact: 'Please enter a phone number or email',
      phone: 'Please check the phone number',
      email: 'Please check the email address',
      generic: 'Something went wrong. Please try again or give us a call.',
    },
  },

  es: {
    langEn: 'EN', langEs: 'ES',
    docTitle: 'Seguro de vida — Danae Marquez',
    hero: {
      initials: 'DM',
      name: 'Danae Marquez',
      quote: 'El seguro de vida no es para quien se va. Es para los que se quedan.',
      statNumber: '~100 millones',
      statText: 'de adultos en EE. UU. no tienen cobertura o no les alcanza. La mayoría cree que cuesta 3 veces más de lo que realmente cuesta.',
      forkLabel: 'Dos razones por las que está en esta página —',
      ctaQuote: 'Proteger a mi familia ↓',
      ctaJoin: 'Unirme al equipo ↓',
    },
    quote: {
      eyebrow: 'GRATIS, SIN COMPROMISO',
      title: 'Vea cuánto cuesta en realidad',
      body: 'A muchas familias les sorprende lo poco que cuesta protegerse. Cuatro preguntas, 30 segundos, y le llamamos con números reales.',
      name: 'Nombre completo', phone: 'Teléfono', email: 'Correo electrónico', age: 'Edad',
      interestLabel: '¿Qué está buscando?',
      interest: { term: 'Seguro a término', whole: 'Vida entera', final_expense: 'Gastos finales', not_sure: 'Aún no estoy seguro' },
      submit: 'Enviar mis datos',
      sending: 'Enviando…',
      finePrint: 'Al enviar, acepta que lo contactemos sobre cobertura. Sin spam y sin compromiso.',
      success: 'Listo. Le llamamos pronto.',
    },
    trust: [
      { title: 'Cobertura que cabe en el presupuesto', body: 'Opciones de varias aseguradoras, no el catálogo de una sola.' },
      { title: 'Atención en español e inglés', body: 'Cada conversación y cada documento explicados en su idioma.' },
      { title: 'Respuestas claras y rápidas', body: 'Llame, mande mensaje o escriba y le contesta una persona real de nuestro equipo.' },
    ],
    join: {
      badge: 'Estamos creciendo',
      title: 'Construya algo propio — y gane ingresos mientras lo hace',
      body: 'Estamos formando un equipo de agentes de vida con licencia. No necesita experiencia — lo guiamos en la licencia, la capacitación y sus primeras citas.',
      boxes: [
        { label: 'Ayuda con la licencia', value: 'Paso a paso' },
        { label: 'Capacitación en vivo', value: 'Cada semana' },
        { label: 'Horario flexible', value: 'Tiempo completo o parcial' },
        { label: 'Equipo bilingüe', value: 'EN / ES' },
      ],
      name: 'Nombre completo', phone: 'Teléfono', email: 'Correo electrónico',
      licensedLabel: '¿Tiene licencia?',
      licensedYes: 'Sí',
      licensedNo: 'No, pero me interesa',
      about: 'Cuéntenos un poco sobre usted',
      submit: 'Hablemos',
      sending: 'Enviando…',
      affiliation: 'Esta consulta es para una oportunidad de contratista independiente (1099) ofrecida por un afiliado independiente de PHP.',
      success: 'Listo. Lo contactamos pronto.',
    },
    footer: {
      name: 'Danae Marquez',
      title: 'Productora de seguros de vida con licencia · Arizona',
      license: 'Licencia #22213864',
      call: 'Llamar', text: 'Mensaje', email: 'Correo',
      disclaimer: 'Los productos de seguros se ofrecen de forma separada y no están afiliados con los servicios de bienes raíces de HomePros ni HomePros Advantage.',
    },
    errors: {
      name: 'Escriba su nombre',
      contact: 'Escriba un teléfono o un correo electrónico',
      phone: 'Revise el número de teléfono',
      email: 'Revise el correo electrónico',
      generic: 'Algo salió mal. Intente de nuevo o llámenos.',
    },
  },
}

const INTEREST_KEYS = ['term', 'whole', 'final_expense', 'not_sure']

const digitsOf = (s) => String(s || '').replace(/\D/g, '')
const emailOk = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(s || '').trim())

// ─── Shared styles ───────────────────────────────────────────────────────────
const inner = { width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }
const navyBleed = { background: NAVY, width: '100%' }
const label = { display: 'block', fontSize: '12px', fontWeight: 400, color: SECONDARY, marginBottom: '6px' }
const field = {
  width: '100%', boxSizing: 'border-box', background: WHITE,
  border: `0.5px solid ${HAIRLINE}`, borderRadius: '8px',
  padding: '12px 14px', fontSize: '15px', fontWeight: 400, color: NAVY,
  outline: 'none', fontFamily: 'inherit',
}
const goldBtn = {
  width: '100%', boxSizing: 'border-box', background: GOLD, color: NAVY,
  border: 'none', borderRadius: '8px', padding: '14px',
  fontSize: '15px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
}
// Off-screen rather than display:none — a bot that skips hidden inputs is the
// one this catches, and display:none is the first thing they skip.
const honeypot = {
  position: 'absolute', left: '-9999px', width: '1px', height: '1px',
  opacity: 0, pointerEvents: 'none',
}
const chip = (selected) => ({
  border: `1px solid ${selected ? BLUE : HAIRLINE}`,
  color: selected ? BLUE : SECONDARY,
  background: WHITE,
  borderRadius: '20px', padding: '7px 14px',
  fontSize: '13px', fontWeight: 400, cursor: 'pointer',
  fontFamily: 'inherit', whiteSpace: 'nowrap',
})
const card = { background: WHITE, border: `0.5px solid ${HAIRLINE}`, borderRadius: '8px', padding: '18px' }

export default function ProtectLanding() {
  const [lang, setLang] = useState('en')
  const t = T[lang] || T.en

  useEffect(() => { document.title = t.docTitle }, [t.docTitle])

  const scrollTo = (id) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  // ── Quote form ─────────────────────────────────────────────────────────────
  const [q, setQ] = useState({ name: '', phone: '', email: '', age: '', interest: null, website: '' })
  const [qState, setQState] = useState({ state: 'idle', msg: '' })

  // ── Recruit form ───────────────────────────────────────────────────────────
  const [j, setJ] = useState({ name: '', phone: '', email: '', licensed: null, about: '', website: '' })
  const [jState, setJState] = useState({ state: 'idle', msg: '' })

  // Shared validation. Order matters: name, then "some way to reach you", then
  // the shape of whichever contact fields were actually filled in.
  const validate = (form) => {
    if (!form.name.trim()) return t.errors.name
    const hasPhone = !!form.phone.trim()
    const hasEmail = !!form.email.trim()
    if (!hasPhone && !hasEmail) return t.errors.contact
    if (hasPhone && digitsOf(form.phone).length < 10) return t.errors.phone
    if (hasEmail && !emailOk(form.email)) return t.errors.email
    return null
  }

  // Row first, then the FUB push — the shape OpenHouseLanding uses. A FUB
  // failure must NOT affect the user-facing success message: the row is already
  // saved and carries sent_to_fub = false, so it stays recoverable.
  const submit = async (row, setState) => {
    // Client-generated id: RETURNING would require a SELECT policy anon doesn't have.
    const { error } = await supabase.from('insurance_leads').insert(row)
    if (error) throw error

    setState({ state: 'success', msg: '' })

    supabase.functions
      .invoke('send-insurance-lead-to-fub', { body: { lead_id: row.id } })
      .then(({ data, error: fnErr }) => {
        if (fnErr) console.error('[fub push] invoke failed:', fnErr)
        else if (data?.error) console.error('[fub push] function returned error:', data.error)
      })
      .catch((e) => console.error('[fub push] threw:', e))
  }

  const submitQuote = async (e) => {
    e.preventDefault()
    if (qState.state === 'sending') return
    const err = validate(q)
    if (err) { setQState({ state: 'error', msg: err }); return }

    // Honeypot. A filled hidden field is a bot, so it gets the success screen
    // and nothing is written. Telling it apart from a real submission is the
    // one thing we do not want to help it do.
    if (q.website.trim()) { setQState({ state: 'success', msg: '' }); return }

    setQState({ state: 'sending', msg: '' })
    try {
      // Nulls are omitted so the column defaults apply. An age outside 18–99 is
      // dropped rather than sent: the DB CHECK would reject the whole insert,
      // and losing an optional field beats losing the lead.
      const ageNum = parseInt(q.age, 10)
      const id = crypto.randomUUID()
      const row = {
        id,
        lead_type: 'quote',
        source: 'insurance-quote',
        name: q.name.trim(),
        language: lang,
      }
      if (q.phone.trim()) row.phone = q.phone.trim()
      if (q.email.trim()) row.email = q.email.trim()
      if (Number.isInteger(ageNum) && ageNum >= 18 && ageNum <= 99) row.age = ageNum
      if (q.interest) row.product_interest = q.interest
      await submit(row, setQState)
    } catch (e2) {
      console.error('[insurance_leads] quote insert failed:', e2)
      setQState({ state: 'error', msg: t.errors.generic })
    }
  }

  const submitJoin = async (e) => {
    e.preventDefault()
    if (jState.state === 'sending') return
    const err = validate(j)
    if (err) { setJState({ state: 'error', msg: err }); return }
    if (j.website.trim()) { setJState({ state: 'success', msg: '' }); return }

    setJState({ state: 'sending', msg: '' })
    try {
      const id = crypto.randomUUID()
      const row = {
        id,
        lead_type: 'recruit',
        source: 'insurance-recruit',
        name: j.name.trim(),
        language: lang,
      }
      if (j.phone.trim()) row.phone = j.phone.trim()
      if (j.email.trim()) row.email = j.email.trim()
      // Untouched stays null — "didn't answer" is not the same as "no".
      if (j.licensed !== null) row.is_licensed = j.licensed
      if (j.about.trim()) row.about = j.about.trim()
      await submit(row, setJState)
    } catch (e2) {
      console.error('[insurance_leads] recruit insert failed:', e2)
      setJState({ state: 'error', msg: t.errors.generic })
    }
  }

  const errLine = (state) => state.state === 'error' && state.msg
    ? <div style={{ fontSize: '13px', color: '#C8102E', marginTop: '10px' }}>{state.msg}</div>
    : null

  const langToggle = (
    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '3px', fontSize: '12px' }}>
      <span
        onClick={() => setLang('en')}
        style={{ padding: '5px 12px', borderRadius: '20px', cursor: 'pointer', ...(lang === 'en' ? { background: 'rgba(255,255,255,0.92)', color: NAVY, fontWeight: 500 } : { color: ON_NAVY_BODY }) }}>
        {t.langEn}
      </span>
      <span
        onClick={() => setLang('es')}
        style={{ padding: '5px 12px', borderRadius: '20px', cursor: 'pointer', ...(lang === 'es' ? { background: 'rgba(255,255,255,0.92)', color: NAVY, fontWeight: 500 } : { color: ON_NAVY_BODY }) }}>
        {t.langEs}
      </span>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: PAGE, fontFamily: "'Barlow', Helvetica, Arial, sans-serif", color: NAVY }}>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ ...navyBleed, padding: '22px 0 34px' }}>
        <div style={inner}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: GOLD, color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 500, flexShrink: 0 }}>
                {t.hero.initials}
              </div>
              <div style={{ fontSize: '15px', fontWeight: 500, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.hero.name}
              </div>
            </div>
            {langToggle}
          </div>

          <p style={{ fontSize: '21px', fontWeight: 400, lineHeight: 1.4, color: WHITE, margin: '26px 0 0' }}>
            {t.hero.quote}
          </p>

          <div style={{ borderLeft: `2px solid ${GOLD}`, paddingLeft: '14px', margin: '26px 0 0' }}>
            <div style={{ fontSize: '24px', fontWeight: 500, color: GOLD, lineHeight: 1.2 }}>{t.hero.statNumber}</div>
            <div style={{ fontSize: '13.5px', fontWeight: 400, lineHeight: 1.6, color: ON_NAVY_BODY, marginTop: '6px' }}>{t.hero.statText}</div>
          </div>

          <div style={{ fontSize: '13.5px', fontWeight: 400, color: ON_NAVY_BODY, margin: '28px 0 12px' }}>
            {t.hero.forkLabel}
          </div>

          <button type="button" onClick={() => scrollTo('quote')} style={goldBtn}>
            {t.hero.ctaQuote}
          </button>
          <button
            type="button"
            onClick={() => scrollTo('join')}
            style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', color: WHITE, border: `1px solid ${ON_NAVY_BODY}`, borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: 400, cursor: 'pointer', fontFamily: 'inherit', marginTop: '10px' }}>
            {t.hero.ctaJoin}
          </button>
        </div>
      </section>

      {/* ── QUOTE FORM ───────────────────────────────────────────────────── */}
      <section id="quote" style={{ scrollMarginTop: '16px', padding: '34px 0' }}>
        <div style={inner}>
          <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '1.4px', color: MUTED, marginBottom: '10px' }}>
            {t.quote.eyebrow}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 500, lineHeight: 1.3, margin: '0 0 10px' }}>{t.quote.title}</h2>
          <p style={{ fontSize: '14px', fontWeight: 400, lineHeight: 1.65, color: SECONDARY, margin: '0 0 22px' }}>{t.quote.body}</p>

          {qState.state === 'success' ? (
            <div style={{ ...card, fontSize: '15px', fontWeight: 400, lineHeight: 1.6 }}>{t.quote.success}</div>
          ) : (
            <form onSubmit={submitQuote} style={{ ...card, position: 'relative' }}>
              <input
                type="text" name="website" value={q.website}
                onChange={(e) => setQ({ ...q, website: e.target.value })}
                style={honeypot} tabIndex={-1} autoComplete="off" aria-hidden="true"
              />

              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="q-name">{t.quote.name}</label>
                <input id="q-name" type="text" style={field} value={q.name}
                  onChange={(e) => setQ({ ...q, name: e.target.value })} autoComplete="name" />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="q-phone">{t.quote.phone}</label>
                <input id="q-phone" type="tel" inputMode="tel" style={field} value={q.phone}
                  onChange={(e) => setQ({ ...q, phone: e.target.value })} autoComplete="tel" />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="q-email">{t.quote.email}</label>
                <input id="q-email" type="email" inputMode="email" style={field} value={q.email}
                  onChange={(e) => setQ({ ...q, email: e.target.value })}
                  autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={label} htmlFor="q-age">{t.quote.age}</label>
                <input id="q-age" type="number" inputMode="numeric" min="18" max="99" style={field} value={q.age}
                  onChange={(e) => setQ({ ...q, age: e.target.value })} />
              </div>

              <div style={{ marginBottom: '18px' }}>
                <div style={label}>{t.quote.interestLabel}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {INTEREST_KEYS.map((k) => (
                    <button key={k} type="button" style={chip(q.interest === k)}
                      onClick={() => setQ({ ...q, interest: q.interest === k ? null : k })}>
                      {t.quote.interest[k]}
                    </button>
                  ))}
                </div>
              </div>

              <button type="submit" style={{ ...goldBtn, opacity: qState.state === 'sending' ? 0.6 : 1 }} disabled={qState.state === 'sending'}>
                {qState.state === 'sending' ? t.quote.sending : t.quote.submit}
              </button>
              {errLine(qState)}
              <p style={{ fontSize: '11.5px', fontWeight: 400, lineHeight: 1.6, color: MUTED, margin: '14px 0 0' }}>
                {t.quote.finePrint}
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ── TRUST ────────────────────────────────────────────────────────── */}
      <section style={{ background: INSET, width: '100%', padding: '30px 0' }}>
        <div style={inner}>
          {t.trust.map((item, i) => (
            <div key={i} style={{ paddingTop: i === 0 ? 0 : '18px', marginTop: i === 0 ? 0 : '18px', borderTop: i === 0 ? 'none' : `0.5px solid ${HAIRLINE}` }}>
              <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '5px' }}>{item.title}</div>
              <div style={{ fontSize: '13.5px', fontWeight: 400, lineHeight: 1.6, color: SECONDARY }}>{item.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── RECRUIT ──────────────────────────────────────────────────────── */}
      <section id="join" style={{ ...navyBleed, scrollMarginTop: '16px', padding: '34px 0' }}>
        <div style={inner}>
          <div style={{ display: 'inline-block', background: GOLD, color: NAVY, borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: 500, marginBottom: '14px' }}>
            {t.join.badge}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 500, lineHeight: 1.3, color: WHITE, margin: '0 0 10px' }}>{t.join.title}</h2>
          <p style={{ fontSize: '14px', fontWeight: 400, lineHeight: 1.65, color: ON_NAVY_BODY, margin: '0 0 20px' }}>{t.join.body}</p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '22px' }}>
            {t.join.boxes.map((b, i) => (
              <div key={i} style={{ border: `1px solid rgba(255,255,255,0.16)`, borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: WHITE }}>{b.label}</div>
                <div style={{ fontSize: '12px', fontWeight: 400, color: ON_NAVY_BODY, marginTop: '3px' }}>{b.value}</div>
              </div>
            ))}
          </div>

          {jState.state === 'success' ? (
            <div style={{ ...card, fontSize: '15px', fontWeight: 400, lineHeight: 1.6 }}>{t.join.success}</div>
          ) : (
            <form onSubmit={submitJoin} style={{ ...card, position: 'relative' }}>
              <input
                type="text" name="website" value={j.website}
                onChange={(e) => setJ({ ...j, website: e.target.value })}
                style={honeypot} tabIndex={-1} autoComplete="off" aria-hidden="true"
              />

              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="j-name">{t.join.name}</label>
                <input id="j-name" type="text" style={field} value={j.name}
                  onChange={(e) => setJ({ ...j, name: e.target.value })} autoComplete="name" />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="j-phone">{t.join.phone}</label>
                <input id="j-phone" type="tel" inputMode="tel" style={field} value={j.phone}
                  onChange={(e) => setJ({ ...j, phone: e.target.value })} autoComplete="tel" />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={label} htmlFor="j-email">{t.join.email}</label>
                <input id="j-email" type="email" inputMode="email" style={field} value={j.email}
                  onChange={(e) => setJ({ ...j, email: e.target.value })}
                  autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div style={label}>{t.join.licensedLabel}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button type="button" style={chip(j.licensed === true)}
                    onClick={() => setJ({ ...j, licensed: j.licensed === true ? null : true })}>
                    {t.join.licensedYes}
                  </button>
                  <button type="button" style={chip(j.licensed === false)}
                    onClick={() => setJ({ ...j, licensed: j.licensed === false ? null : false })}>
                    {t.join.licensedNo}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={label} htmlFor="j-about">{t.join.about}</label>
                <textarea id="j-about" rows={4} style={{ ...field, resize: 'vertical' }} value={j.about}
                  onChange={(e) => setJ({ ...j, about: e.target.value })} />
              </div>

              <button type="submit" style={{ ...goldBtn, opacity: jState.state === 'sending' ? 0.6 : 1 }} disabled={jState.state === 'sending'}>
                {jState.state === 'sending' ? t.join.sending : t.join.submit}
              </button>
              {errLine(jState)}
            </form>
          )}

          <p style={{ fontSize: '11.5px', fontWeight: 400, lineHeight: 1.6, color: MUTED, margin: '14px 0 0' }}>
            {t.join.affiliation}
          </p>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer style={{ padding: '30px 0 40px' }}>
        <div style={inner}>
          <div style={{ fontSize: '16px', fontWeight: 500 }}>{t.footer.name}</div>
          <div style={{ fontSize: '13px', fontWeight: 400, color: SECONDARY, marginTop: '4px' }}>{t.footer.title}</div>
          <div style={{ fontSize: '13px', fontWeight: 400, color: MUTED, marginTop: '2px' }}>{t.footer.license}</div>

          {(DANAE_PHONE || DANAE_EMAIL) && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              {DANAE_PHONE && (
                <a href={`tel:${DANAE_PHONE}`} style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: GOLD, color: NAVY, borderRadius: '8px', padding: '12px 0', fontSize: '14px', fontWeight: 500 }}>
                  {t.footer.call}
                </a>
              )}
              {DANAE_PHONE && (
                <a href={`sms:${DANAE_PHONE}`} style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: GOLD, color: NAVY, borderRadius: '8px', padding: '12px 0', fontSize: '14px', fontWeight: 500 }}>
                  {t.footer.text}
                </a>
              )}
              {DANAE_EMAIL && (
                <a href={`mailto:${DANAE_EMAIL}`} style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: GOLD, color: NAVY, borderRadius: '8px', padding: '12px 0', fontSize: '14px', fontWeight: 500 }}>
                  {t.footer.email}
                </a>
              )}
            </div>
          )}

          <p style={{ fontSize: '11.5px', fontWeight: 400, lineHeight: 1.65, color: MUTED, margin: '22px 0 0', paddingTop: '18px', borderTop: `0.5px solid ${HAIRLINE}` }}>
            {t.footer.disclaimer}
          </p>
        </div>
      </footer>
    </div>
  )
}
